// ABOUTME: Unified report content renderer with single TipTap document for text flow.
// ABOUTME: Combines text/heading sections into one editor, renders atomic sections inline.

import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import { Extension } from '@tiptap/core';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import type { ReportSection, TableData } from '@/types/api';
import { Trash2 } from 'lucide-react';
import { ChartSection } from './ChartSection';
import { TableSection } from './TableSection';
import { InsightReferenceSection } from './InsightReferenceSection';
import { INSIGHT_DRAG_TYPE, INSIGHT_REF_DRAG_TYPE } from './AddInsightPanel';
import { apiClient } from '@/services/api';

// Custom font size extension
const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    } as const;
  },
});

interface TextSelection {
  sectionId: string;
  startOffset: number;
  endOffset: number;
  selectedText: string;
  rect?: DOMRect;
}

export interface UnifiedReportContentRef {
  getEditor: () => Editor | null;
}

interface UnifiedReportContentProps {
  sections: ReportSection[];
  reportId: string;
  reportVersion: number;
  /** Ref to the latest version, avoids stale closures in async handlers */
  versionRef: React.RefObject<number>;
  onSectionUpdate: (sectionId: string, content: string) => void;
  onSectionDelete: (sectionId: string) => void;
  onSelect: (selection: TextSelection | null) => void;
  onEditorReady: (editor: Editor | null) => void;
  onVersionUpdate: (version: number) => void;
  /** Merge server-persisted fields into local section state (e.g. after PATCH table-data). */
  onPersistedSectionPatch?: (sectionId: string, patch: Partial<ReportSection>) => void;
  /** Flushes pending report-level saves before atomic section PATCHes that use If-Match. */
  onBeforeAtomicSectionSave?: () => Promise<void>;
  onRefetch: () => void;
  /** When set, enables positional drop zones between sections for insight drag-and-drop */
  onInsightDrop?: (pinId: string, insertAtOrder: number) => void;
  /** Whether an insight is currently being dragged over the editor */
  isDraggingInsight?: boolean;
}

// Text Flow Editor Component for a single text section
interface TextFlowEditorProps {
  textSections: ReportSection[];
  onSectionUpdate: (sectionId: string, content: string) => void;
  onSelect: (selection: TextSelection | null) => void;
  onEditorReady: (editor: Editor | null) => void;
}

function TextFlowEditor({
  textSections,
  onSectionUpdate,
  onSelect,
  onEditorReady,
}: TextFlowEditorProps) {
  const isExternalUpdate = useRef(false);
  const sectionsRef = useRef(textSections);
  const editorInstanceRef = useRef<Editor | null>(null);
  // Track the last content the editor emitted via onUpdate (from user typing).
  // When incoming prop content differs from this, it's an external change (undo/redo, Magic Edit).
  const lastEmittedContent = useRef(textSections[0]?.content || '<p></p>');

  // Update ref when sections change
  useEffect(() => {
    sectionsRef.current = textSections;
  }, [textSections]);

  const initialHtml = useMemo(
    () => {
      return textSections[0]?.content || '<p></p>';
    },
    // Only compute initial HTML once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleEditorChange = useCallback(
    (html: string) => {
      if (isExternalUpdate.current) return;

      const section = sectionsRef.current[0];
      if (!section) return;

      if (section.content !== html) {
        onSectionUpdate(section.id, html);
      }
    },
    [onSectionUpdate]
  );

  const handleSelectionChange = useCallback(
    (editor: Editor) => {
      const { from, to, empty } = editor.state.selection;

      if (empty) {
        onSelect(null);
        return;
      }

      const selectedText = editor.state.doc.textBetween(from, to, '');
      if (!selectedText.trim()) {
        onSelect(null);
        return;
      }

      // Since this editor handles only ONE section, use that section's ID
      const section = sectionsRef.current[0];
      if (!section) {
        onSelect(null);
        return;
      }

      const domSelection = window.getSelection();
      if (!domSelection || domSelection.rangeCount === 0) {
        onSelect(null);
        return;
      }

      const range = domSelection.getRangeAt(0);

      // Calculate offset within the editor's text content
      let startOffset = 0;
      const walker = document.createTreeWalker(
        editor.view.dom,
        NodeFilter.SHOW_TEXT,
        null
      );

      let textNode;
      while ((textNode = walker.nextNode())) {
        if (textNode === range.startContainer) {
          startOffset += range.startOffset;
          break;
        }
        startOffset += textNode.textContent?.length || 0;
      }

      onSelect({
        sectionId: section.id,
        startOffset,
        endOffset: startOffset + selectedText.length,
        selectedText,
        rect: range.getBoundingClientRect(),
      });
    },
    [onSelect]
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        trailingNode: false,
        // Note: TipTap's history is still active but keyboard shortcuts are intercepted by ReportEditor
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        defaultAlignment: 'justify',
      }),
    ],
    content: initialHtml,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastEmittedContent.current = html;
      handleEditorChange(html);
    },
    editorProps: {
      attributes: {
        class:
          'outline-none prose prose-invert max-w-none prose-headings:text-obsidian-text-primary prose-p:text-obsidian-text-secondary prose-p:leading-relaxed prose-li:text-obsidian-text-secondary',
      },
      handleDOMEvents: {
        mouseup: () => {
          setTimeout(() => {
            if (editorInstanceRef.current) {
              handleSelectionChange(editorInstanceRef.current);
            }
          }, 0);
          return false;
        },
      },
    },
  });

  // Store ref for mouseup handler
  const onEditorReadyRef = useRef(onEditorReady);
  onEditorReadyRef.current = onEditorReady;

  useEffect(() => {
    editorInstanceRef.current = editor;
    // Don't call onEditorReady on mount - only call it when user focuses the editor
    // This prevents the "last editor wins" problem where the last mounted editor
    // becomes active even though user hasn't interacted with it
    return () => {
      editorInstanceRef.current = null;
    };
  }, [editor]);

  // Handle focus to notify parent which editor is active
  useEffect(() => {
    if (!editor) return;

    const handleFocus = () => {
      onEditorReadyRef.current(editor);
    };

    editor.on('focus', handleFocus);
    return () => {
      editor.off('focus', handleFocus);
    };
  }, [editor]);

  // Sync external content changes (undo/redo, Magic Edit, AI Edit) into the editor.
  // Compares incoming prop content against lastEmittedContent to distinguish
  // echoes (editor's own typing bounced back via state) from genuine external updates.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    const section = textSections[0];
    if (!section) return;

    const incomingContent = section.content;

    // If incoming matches what the editor last emitted, it's an echo of our own edit — skip
    if (incomingContent === lastEmittedContent.current) return;

    // External change — push it into the editor
    lastEmittedContent.current = incomingContent ?? '';
    try {
      isExternalUpdate.current = true;
      editor.commands.setContent(incomingContent || '', { emitUpdate: false });
    } catch {
      // Recover gracefully
    } finally {
      isExternalUpdate.current = false;
    }
  }, [textSections, editor]);

  if (!editor) return null;

  return <EditorContent editor={editor} className="tiptap-unified" />;
}

/** Drop zone rendered between sections. Invisible until an insight is dragged over it. */
function SectionDropZone({
  insertAtOrder,
  onDrop,
  isVisible,
}: {
  insertAtOrder: number;
  onDrop: (pinId: string, order: number) => void;
  /** Whether an insight drag is in progress (show expanded hit target) */
  isVisible: boolean;
}) {
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(INSIGHT_DRAG_TYPE) || e.dataTransfer.types.includes(INSIGHT_REF_DRAG_TYPE)) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
      setIsOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setIsOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
    const pinId = e.dataTransfer.getData(INSIGHT_DRAG_TYPE);
    if (pinId) {
      onDrop(pinId, insertAtOrder);
      return;
    }
    // For unpinned insights, pass the ref data through the same handler
    // The parent (ReportEditor) will detect this via the handleDropData helper
    const refData = e.dataTransfer.getData(INSIGHT_REF_DRAG_TYPE);
    if (refData) {
      // Use a special prefixed ID so the parent can detect it's a ref
      onDrop(`__ref__${refData}`, insertAtOrder);
    }
  }, [onDrop, insertAtOrder]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className="relative"
      style={{
        // When dragging, expand the hit target; otherwise collapse
        height: isVisible ? (isOver ? 40 : 20) : 0,
        transition: 'height 150ms ease',
      }}
    >
      {/* The blue line + pill indicator */}
      <div
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none"
        style={{
          opacity: isOver ? 1 : 0,
          transition: 'opacity 150ms ease',
        }}
      >
        <div className="absolute inset-x-0 h-0.5 bg-primary-500 rounded-full" />
        <span className="relative bg-primary-600 text-white text-[11px] font-medium px-3 py-1 rounded-full shadow-sm z-10">
          Insert here
        </span>
      </div>
    </div>
  );
}

export const UnifiedReportContent = forwardRef<
  UnifiedReportContentRef,
  UnifiedReportContentProps
>(
  (
    {
      sections,
      reportId,
      reportVersion,
      versionRef,
      onSectionUpdate,
      onSectionDelete,
      onSelect,
      onEditorReady,
      onVersionUpdate,
      onPersistedSectionPatch,
      onBeforeAtomicSectionSave,
      onRefetch,
      onInsightDrop,
      isDraggingInsight,
    },
    ref
  ) => {
    const editorRef = useRef<Editor | null>(null);

    const sortedSections = useMemo(
      () => [...sections].sort((a, b) => a.order - b.order),
      [sections]
    );

    // Separate the title section (first heading at order 0) from other sections
    // Title renders above columns with column-span: all, rest flows in columns
    const { titleSection, contentSections } = useMemo(() => {
      if (sortedSections.length === 0) {
        return { titleSection: null, contentSections: [] };
      }
      const first = sortedSections[0];
      if (first.type === 'heading' && first.order === 0) {
        return {
          titleSection: first,
          contentSections: sortedSections.slice(1),
        };
      }
      return { titleSection: null, contentSections: sortedSections };
    }, [sortedSections]);

    // For true newspaper flow, we render sections sequentially in order
    // Text sections use TipTap, atomic sections render as React components
    // All within the same column container so CSS columns flow everything together
    const isTextSection = useCallback((section: ReportSection) => {
      return section.type === 'text' || section.type === 'heading';
    }, []);

    const handleEditorReady = useCallback(
      (editor: Editor | null) => {
        editorRef.current = editor;
        onEditorReady(editor);
      },
      [onEditorReady]
    );

    // Render atomic section (chart, table, insight_reference)
    const renderAtomicSection = useCallback(
      (section: ReportSection) => {
        if (section.type === 'visualization') {
          // TODO(2026-03-04, claude): This table_data conversion logic is duplicated
          // in ReportEditorCore.tsx. Extract into a shared utility function
          // (e.g. utils/table-data.ts) to avoid drift between the two renderers.
          // Convert backend table_data format to ReportTable format
          const tableData = section.table_data ? (() => {
            const cols = (section.table_data.columns || []) as { id?: string; header?: string; key?: string }[];
            const headers = cols.map(c => c.header || c.key || c.id || '');
            const colIds = cols.map(c => c.id || c.key || c.header || '');
            const rows = ((section.table_data.rows || []) as Record<string, unknown>[]).map(row =>
              colIds.map(id => row[id] ?? '')
            ) as (string | number)[][];
            return { headers, rows };
          })() : null;

          // Build onTableSave handler for converted chart-table sections
          const handleTableSave = section.table_data ? async (data: { title: string; headers: string[]; rows: (string | number)[][] }) => {
            await onBeforeAtomicSectionSave?.();
            const totalRows = data.rows.length;
            const tableConfig = {
              columns: data.headers.map((header: string, i: number) => ({ id: `col_${i}`, header, type: 'text' })),
              rows: data.rows.map((row: (string | number)[]) => {
                const rowObj: Record<string, unknown> = {};
                row.forEach((cell, i) => { rowObj[`col_${i}`] = cell; });
                return rowObj;
              }),
              max_display_rows: section.table_data?.max_display_rows ?? totalRows,
            };
            const response = await apiClient.reports.updateTableData(reportId, section.id, tableConfig, { ifMatch: versionRef.current });
            if (onVersionUpdate) onVersionUpdate(response.version);
            return { version: response.version };
          } : undefined;

          // Handler for expand/collapse toggle — updates max_display_rows in table_data
          const handleMaxDisplayRowsChange = section.table_data ? async (maxRows: number) => {
            try {
              await onBeforeAtomicSectionSave?.();
              const tableConfig = { ...section.table_data, max_display_rows: maxRows };
              const response = await apiClient.reports.updateTableData(reportId, section.id, tableConfig, { ifMatch: versionRef.current });
              if (onVersionUpdate) onVersionUpdate(response.version);
              if (response.converted_from_reference) {
                onRefetch();
              } else {
                onPersistedSectionPatch?.(section.id, { table_data: tableConfig as TableData });
              }
            } catch {
              // PATCH failed — persisted state diverges from local until page reload or successful retry
              console.warn(`Failed to persist max_display_rows for section ${section.id}`);
            }
          } : undefined;

          return (
            <ChartSection
              reportId={reportId}
              sectionId={section.id}
              reportVersion={reportVersion}
              content={section.content}
              visualization={section.visualization}
              tableData={tableData}
              maxDisplayRows={section.table_data?.max_display_rows}
              onTableSave={handleTableSave}
              onMaxDisplayRowsChange={handleMaxDisplayRowsChange}
              onUpdate={(content) => onSectionUpdate(section.id, content)}
              onDelete={() => onSectionDelete(section.id)}
              onVersionUpdate={onVersionUpdate}
              onRefetch={onRefetch}
            />
          );
        }

        if (section.type === 'table') {
          const handleTableSectionSave = section.table_data ? async (data: { title: string; headers: string[]; rows: (string | number)[][] }) => {
            await onBeforeAtomicSectionSave?.();
            const totalRows = data.rows.length;
            const tableConfig = {
              columns: data.headers.map((header: string, i: number) => ({ id: `col_${i}`, header, type: 'text' })),
              rows: data.rows.map((row: (string | number)[]) => {
                const rowObj: Record<string, unknown> = {};
                row.forEach((cell, i) => { rowObj[`col_${i}`] = cell; });
                return rowObj;
              }),
              max_display_rows: section.table_data?.max_display_rows ?? totalRows,
            };
            const response = await apiClient.reports.updateTableData(reportId, section.id, tableConfig, { ifMatch: versionRef.current });
            if (onVersionUpdate) onVersionUpdate(response.version);
            return { version: response.version };
          } : undefined;

          // Handler for expand/collapse toggle
          const handleTableMaxDisplayRowsChange = section.table_data ? async (maxRows: number) => {
            try {
              await onBeforeAtomicSectionSave?.();
              const tableConfig = { ...section.table_data, max_display_rows: maxRows };
              const response = await apiClient.reports.updateTableData(reportId, section.id, tableConfig, { ifMatch: versionRef.current });
              if (onVersionUpdate) onVersionUpdate(response.version);
              if (response.converted_from_reference) {
                onRefetch();
              } else {
                onPersistedSectionPatch?.(section.id, { table_data: tableConfig as TableData });
              }
            } catch {
              // PATCH failed — persisted state diverges from local until page reload or successful retry
              console.warn(`Failed to persist max_display_rows for section ${section.id}`);
            }
          } : undefined;

          return (
            <TableSection
              content={section.content}
              tableData={section.table_data}
              onUpdate={(content) => onSectionUpdate(section.id, content)}
              onDelete={() => onSectionDelete(section.id)}
              onTableSave={handleTableSectionSave}
              onMaxDisplayRowsChange={handleTableMaxDisplayRowsChange}
            />
          );
        }

        if (section.type === 'insight_reference' && section.insight_ref) {
          const handleTableExpandedChange = async (expanded: boolean) => {
            try {
              const response = await apiClient.reports.updateTableExpanded(
                reportId, section.id, expanded, { ifMatch: versionRef.current }
              );
              if (onVersionUpdate) onVersionUpdate(response.version);
              onPersistedSectionPatch?.(section.id, { table_expanded: expanded });
            } catch {
              console.warn(`Failed to persist table_expanded for section ${section.id}`);
              onPersistedSectionPatch?.(section.id, { table_expanded: !expanded });
            }
          };

          return (
            <InsightReferenceSection
              reportId={reportId}
              messageId={section.insight_ref.message_id}
              insightId={section.insight_ref.insight_id}
              sectionId={section.id}
              reportVersion={reportVersion}
              versionRef={versionRef}
              editable={true}
              tableExpanded={section.table_expanded}
              onTableExpandedChange={handleTableExpandedChange}
              onBeforeTableSave={onBeforeAtomicSectionSave}
              onConversionComplete={onRefetch}
              onDelete={() => onSectionDelete(section.id)}
            />
          );
        }

        return (
          <div className="text-obsidian-accent-coral text-sm p-4">
            Unknown section type: {section.type}
          </div>
        );
      },
      [reportId, reportVersion, onSectionUpdate, onSectionDelete, onVersionUpdate, onPersistedSectionPatch, onBeforeAtomicSectionSave, onRefetch]
    );

    // Expose methods
    useImperativeHandle(ref, () => ({
      getEditor: () => editorRef.current,
    }));

    // Render title section above columns, then content sections in column flow
    return (
      <div className="unified-report-content">
        {/* Title section - spans full width above columns */}
        {titleSection && (
          <div
            key={titleSection.id}
            className="report-title-section column-span-all mb-6 pb-4 border-b border-obsidian-border/50"
            data-section-id={titleSection.id}
          >
            <TextFlowEditor
              textSections={[titleSection]}
              onSectionUpdate={onSectionUpdate}
              onSelect={onSelect}
              onEditorReady={handleEditorReady}
            />
          </div>
        )}

        {/* Content sections - flow in columns, with drop zones between */}
        {contentSections.map((section, idx) => {
          const sectionEl = isTextSection(section) ? (
            <div key={section.id} className="report-text-section group relative" data-section-id={section.id}>
              <TextFlowEditor
                textSections={[section]}
                onSectionUpdate={onSectionUpdate}
                onSelect={onSelect}
                onEditorReady={handleEditorReady}
              />
              {/* Hover-reveal delete button for text/heading sections */}
              <button
                onClick={() => onSectionDelete(section.id)}
                className="absolute top-1 right-1 p-1.5 rounded-sm bg-surface-50 text-surface-400 hover:text-error hover:bg-error-light transition-all opacity-0 group-hover:opacity-100 cursor-pointer z-10"
                title="Delete section"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div
              key={section.id}
              className="report-section-atomic"
              data-section-id={section.id}
            >
              {renderAtomicSection(section)}
            </div>
          );

          return onInsightDrop ? (
            <div key={section.id}>
              {/* Drop zone before this section */}
              {idx === 0 && (
                <SectionDropZone
                  insertAtOrder={section.order}
                  onDrop={onInsightDrop}
                  isVisible={!!isDraggingInsight}
                />
              )}
              {sectionEl}
              {/* Drop zone after this section */}
              <SectionDropZone
                insertAtOrder={section.order + 1}
                onDrop={onInsightDrop}
                isVisible={!!isDraggingInsight}
              />
            </div>
          ) : sectionEl;
        })}
      </div>
    );
  }
);

UnifiedReportContent.displayName = 'UnifiedReportContent';
